import { IS_NATIVE, platform } from '@/platform';
import { logger } from '@/lib/logger';
import { onSessionWipe } from '@/lib/sessionWipe';
import { underSessionStoreLock } from '@/lib/sessionStoreLock';
import type { ActiveSession } from '@/lib/activeSessionUser';

/**
 * The app's outbox for chat text messages (M10): a message is written here
 * before it is sent, so it survives a lost connection, a lost answer and a
 * force-stop, and is sent exactly once when the connection returns.
 *
 * Exactly once rests on the message id, made on the phone (issue #437) and
 * kept with the entry: every attempt inserts the same id. A retry whose first
 * attempt did reach the server fails on the primary key (23505), and a
 * constraint error is not proof of anything (assessment §R.50), so the outbox
 * then looks the id up as the sender: there, it was sent; not there, it was
 * not, and the message is marked as not sent rather than reported as sent.
 *
 * Nothing is reported as sent until the server has it. A refusal (the
 * moderation trigger, a policy) marks the message "not sent" with the
 * server's reason, for the user to retry or delete. No answer at all leaves
 * it waiting, in order, until the next reconnect, resume or backoff.
 *
 * App only, in secure storage (Keystore-encrypted): it holds what people
 * wrote. Sign-out clears secure storage, outbox included, and entries are
 * only ever sent as the account that wrote them. The website does not queue.
 */

export const CHAT_OUTBOX_KEY = 'outbox.chat';
/** Where an outbox that could not be read is kept, rather than saved over (Codex on #1136). */
export const CHAT_OUTBOX_UNREADABLE_KEY = 'outbox.chat.unreadable';
export const CHAT_OUTBOX_LIMIT = 100;
/**
 * Ids of messages the server refused whose "not sent" state could not be
 * saved with the outbox (Codex on #1136). The saved outbox still says
 * waiting, so a restart would send them on its own; this list, in plain
 * prefs (ids only, no content), keeps them "not sent" until the person
 * chooses Try again.
 */
export const CHAT_REFUSED_PREF = 'chat-outbox.refused';
export const NOT_SENT_PLAIN = 'This message was not sent.';

export type ChatOutboxState = 'queued' | 'failed';

export interface ChatOutboxEntry {
  id: string;
  senderId: string;
  receiverId: string;
  bookingId: string | null;
  content: string;
  /** When it was written: the order entries are sent in, and the bubble's time. */
  createdAt: string;
  state: ChatOutboxState;
  /** Attempts that got no answer since the last success, for the backoff. */
  attempts: number;
  /** The server's reason, for a message it refused. */
  error?: string;
}

export interface ChatOutboxError {
  code?: string | null;
  message: string;
}

/**
 * The server calls. Each is made with the access token of the session the
 * drain has just confirmed is the sender's (Codex on #1137): a sign-in that
 * lands mid-request cannot put another account's token on it.
 */
/** A message as the server stored it: its time, and its content after moderation. */
export interface Stored {
  createdAt: string;
  content?: string;
}

export interface ChatOutboxDeps {
  insert(row: {
    id: string;
    content: string;
    sender_id: string;
    receiver_id: string;
    message_type: 'text';
    booking_id: string | null;
  }, accessToken: string): Promise<{ error: ChatOutboxError | null }>;
  /**
   * The sender's message with this id as the server has it: its server time
   * (the insert trigger sets created_at), false when it is not there, null
   * when that could not be read.
   */
  exists(id: string, senderId: string, accessToken: string): Promise<Stored | false | null>;
  /**
   * The session stored now (lib/activeSessionUser.ts): a message is sent only
   * under its own sender's session (Codex on #1136).
   */
  activeSession(): Promise<ActiveSession | null>;
}

/**
 * Worth waiting out: PostgREST's "could not reach the database" and an
 * expired token, and the database's own passing failures (Codex on #1136):
 * a statement timeout (57014, already treated as intermittent in
 * availabilityCounts.ts), a serialization failure or deadlock (40001,
 * 40P01), too many connections (53300) and a lock not available (55P03).
 */
const TRANSIENT_CODES = new Set([
  'PGRST000', 'PGRST001', 'PGRST002', 'PGRST003', 'PGRST301',
  '57014', '40001', '40P01', '53300', '55P03',
]);

/**
 * The send-rate limit (enforce_message_send_authorization: 30 messages in a
 * rolling five minutes) refuses with 42501 like a policy does, but passes
 * with time: an outbox draining a long offline backlog meets it, and its
 * later messages must wait, not be marked "not sent" (Codex on #1136).
 */
export function isRateLimited(error: ChatOutboxError): boolean {
  return error.code === '42501' && /sending messages too quickly/i.test(error.message);
}

/** No code at all is no answer from the server: fetch itself failed. */
export function isTransientError(error: ChatOutboxError): boolean {
  return !error.code || TRANSIENT_CODES.has(error.code) || isRateLimited(error);
}

export const NOT_SENT_UNCONFIRMED = 'This message could not be confirmed as sent.';
export const NOT_SENT_NOT_ALLOWED = 'You can’t message this person right now.';

/** The reason shown on a refused message: the server's own words, except a bare policy refusal. */
function refusalReason(error: ChatOutboxError | null): string {
  if (error?.code === '42501') return NOT_SENT_NOT_ALLOWED;
  return error?.message || 'Message not sent.';
}

let deps: ChatOutboxDeps | null = null;
let entries: ChatOutboxEntry[] | null = null;
let loading: Promise<ChatOutboxEntry[]> | null = null;
let writes: Promise<void> = Promise.resolve();
let draining: Promise<void> | null = null;
// Every account a drain was asked for and has not yet run a pass for, in the
// order asked: a sign-in as someone else during a drain is not left to wait
// (Codex on #1137), and a stale timer for the account before cannot take its
// place (Codex on #1136). A pass for an account not signed in sends nothing.
const drainSenders = new Set<string>();
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let retryAt = 0;
const retrySenders = new Set<string>();
// Moved on by each sign-out: an enqueue that began before one keeps nothing.
let generation = 0;
const listeners = new Set<(entries: readonly ChatOutboxEntry[]) => void>();
const sentListeners = new Set<(entry: ChatOutboxEntry) => void>();
/**
 * Refused messages whose refusal no store has kept yet (id → reason): neither
 * the outbox save nor the prefs mark succeeded. They are not shown as "not
 * sent" until one does, because a restart before then would send them (Codex
 * on #1136): they stay shown as waiting, and are not sent in this run. Each
 * drain tries again to keep the refusal.
 */
const unsavedRefusals = new Map<string, string>();

/** The refused marks are there but cannot be parsed: which ids they hold is unknown. */
class UnreadableRefusalsError extends Error {
  constructor() {
    super('The refused messages could not be read.');
    this.name = 'UnreadableRefusalsError';
  }
}

/**
 * The refused ids. A prefs read that fails rejects (Codex on #1136): taken
 * as "none", a load would make a message the screen called "not sent"
 * waiting again, and send it. Marks that cannot be parsed reject too, for
 * the same reason (Codex on #1136); the load then counts every waiting
 * message as refused.
 */
async function readRefused(): Promise<Set<string>> {
  const raw = await platform.storage.prefs.get(CHAT_REFUSED_PREF);
  let ids: unknown;
  try {
    ids = raw ? JSON.parse(raw) : [];
  } catch {
    throw new UnreadableRefusalsError();
  }
  // One mark that is not an id is as unreadable as the whole: dropped, it
  // would make its message waiting again, and send it (Codex on #1136).
  if (!Array.isArray(ids) || !ids.every((x) => typeof x === 'string')) throw new UnreadableRefusalsError();
  return new Set(ids);
}

async function writeRefused(ids: Set<string>): Promise<void> {
  try {
    await saveRefused(ids);
  } catch (error) {
    logger.warn('[chat-outbox] could not record a refused message', error);
  }
}

/** Like writeRefused, but a failure rejects, for the caller that must know. */
async function saveRefused(ids: Set<string>): Promise<void> {
  if (ids.size > 0) await platform.storage.prefs.set(CHAT_REFUSED_PREF, JSON.stringify([...ids]));
  else await platform.storage.prefs.remove(CHAT_REFUSED_PREF);
}

let marksChain: Promise<unknown> = Promise.resolve();

/**
 * Every read-modify-write of the refused marks runs alone (as Codex found on
 * #1137): two at once would each save the marks without the other's change,
 * and a restart would then undo a Try again.
 */
function underMarks<T>(task: () => Promise<T>): Promise<T> {
  const run = marksChain.then(task);
  marksChain = run.catch(() => undefined);
  return run;
}

/** Reads the marks, changes them, and saves them if `change` says so, alone. */
function changeRefused(change: (ids: Set<string>) => boolean): Promise<void> {
  return underMarks(async () => {
    const ids = await readRefused();
    if (change(ids)) await saveRefused(ids);
  });
}

/** Like unmarkRefused, but a failure rejects, for the caller that must know. */
async function clearRefusedMark(id: string): Promise<void> {
  await changeRefused((ids) => ids.delete(id));
}

async function unmarkRefused(id: string): Promise<void> {
  try {
    await changeRefused((ids) => ids.delete(id));
  } catch (error) {
    logger.warn('[chat-outbox] could not update the refused messages', error);
  }
}

/**
 * Mark a message "not sent" so that no restart can send it on its own: in the
 * saved outbox, else in prefs, else held for the next save or drain to keep.
 * It is "not sent" in memory at once, so this run never sends it either.
 */
async function markRefused(id: string, reason: string): Promise<void> {
  await keepRefusals(new Map([[id, reason]]));
}

/**
 * Keep refusals so that no restart can send them, then show them "not sent":
 * in the saved outbox, else as marks in prefs. If neither store takes them,
 * they are held (unsavedRefusals): shown as waiting, not sent in this run,
 * and tried again by the next drain.
 */
async function keepRefusals(refusals: Map<string, string>): Promise<void> {
  const refuse = (list: ChatOutboxEntry[]) =>
    list.map((e) => (refusals.has(e.id) ? { ...e, state: 'failed' as const, error: refusals.get(e.id) } : e));
  try {
    if (await commitDurably(refuse)) {
      for (const id of refusals.keys()) unsavedRefusals.delete(id);
      return;
    }
  } catch {
    // Not saved: the marks below.
  }
  try {
    await changeRefused((ids) => {
      for (const id of refusals.keys()) ids.add(id);
      return true;
    });
  } catch (error) {
    logger.warn('[chat-outbox] could not record a refused message; will try again', error);
    hold(refusals);
    return;
  }
  for (const id of refusals.keys()) unsavedRefusals.delete(id);
  if (entries) {
    entries = refuse(entries);
    announce();
  }
}

/** Refusals no store took: shown as waiting, and not sent, until one does. */
function hold(refusals: Map<string, string>): void {
  for (const [id, reason] of refusals) unsavedRefusals.set(id, reason);
  if (!entries?.some((e) => refusals.has(e.id) && e.state === 'failed')) return;
  entries = entries.map((e) => (refusals.has(e.id) ? { ...e, state: 'queued' as const, error: undefined } : e));
  announce();
}

/** Another try at keeping refusals that no store has kept yet. */
async function keepUnsavedRefusals(): Promise<void> {
  if (unsavedRefusals.size === 0) return;
  await keepRefusals(new Map(unsavedRefusals));
}

/**
 * Settle an entry as sent, once: the drain and the realtime echo may both
 * report it. Announced only once its removal is saved (Codex on #1136): a
 * removal that is not saved could bring the entry back after a restart, be
 * confirmed again and be announced twice. Until then it stays waiting, and
 * the next drain settles it again (the resend is found by its id).
 */
async function settleSent(entry: ChatOutboxEntry, stored?: Stored | null): Promise<void> {
  let removed: boolean;
  try {
    removed = await commitDurably((list) =>
      list.some((e) => e.id === entry.id) ? list.filter((e) => e.id !== entry.id) : null,
    );
  } catch (error) {
    logger.warn('[chat-outbox] could not save a sent message; will settle it again', error);
    scheduleRetry(entry.senderId, 1);
    return;
  }
  if (!removed) return;
  // Shown as the server stored it, not as it was kept: the insert trigger
  // sets created_at, the moderation trigger may rewrite the content, and the
  // realtime echo that carries both is dropped as a duplicate once the thread
  // has the message (Codex on #1136).
  const shown = {
    ...entry,
    ...(stored?.createdAt ? { createdAt: stored.createdAt } : {}),
    ...(typeof stored?.content === 'string' ? { content: stored.content } : {}),
  };
  for (const cb of sentListeners) cb(shown);
}

/**
 * The server's own realtime echo of the sender's message proves it arrived:
 * settle its entry as sent instead of showing it twice, once in the thread
 * and once as waiting (Codex on #1136). `sentAt` is the echo's server time.
 */
export async function confirmChatMessage(id: string, sentAt?: string | null): Promise<void> {
  const entry = entries?.find((e) => e.id === id);
  if (entry) await settleSent(entry, sentAt ? { createdAt: sentAt } : null);
}

/** The server calls; set once by the app (and by tests). */
export function configureChatOutbox(next: ChatOutboxDeps): void {
  deps = next;
}

/**
 * The outbox, read once. A store that cannot be read right now rejects and is
 * read again next time: taking it as empty would let the next save overwrite
 * every message still waiting in it (Codex on #1136). Only content that is
 * not an outbox at all starts empty.
 */
async function load(): Promise<ChatOutboxEntry[]> {
  if (entries) return entries;
  loading ??= (async () => {
    const raw = await platform.storage.secure.get(CHAT_OUTBOX_KEY);
    let loaded: ChatOutboxEntry[] = [];
    let parsed: unknown;
    try {
      parsed = raw ? JSON.parse(raw) : [];
    } catch {
      parsed = undefined;
    }
    if (Array.isArray(parsed)) loaded = parsed.filter(isEntry);
    if (!Array.isArray(parsed) || loaded.length !== parsed.length) {
      // Not wholly an outbox this code saved: it is kept aside before
      // anything is saved over it, so no write discards it unread, and until
      // that is kept nothing loads (Codex on #1136). The messages that can be
      // read still go; one that cannot is in the copy kept aside.
      await platform.storage.secure.set(CHAT_OUTBOX_UNREADABLE_KEY, raw ?? '');
      logger.warn('[chat-outbox] part of the outbox could not be read; it is kept aside');
    }
    const refused = await underMarks(async () => {
      try {
        return await readRefused();
      } catch (error) {
        if (!(error instanceof UnreadableRefusalsError)) throw error;
        // Which were refused is unknown: every waiting message counts as
        // refused, shown "not sent" with Try again, rather than sent again,
        // and the marks are rewritten to say so. Until that is saved, nothing
        // loads (Codex on #1136).
        const all = new Set(loaded.filter((e) => e.state === 'queued').map((e) => e.id));
        await saveRefused(all);
        return all;
      }
    });
    if (refused.size > 0) {
      loaded = loaded.map((e) =>
        refused.has(e.id) && e.state === 'queued' ? { ...e, state: 'failed', error: e.error ?? NOT_SENT_PLAIN } : e,
      );
    }
    if (!entries) {
      entries = loaded;
      // A list read on a retry reaches screens that were shown none while
      // the store could not be read (Codex on #1137).
      announce();
    }
    return entries;
  })().finally(() => {
    loading = null;
  });
  return loading;
}

function isEntry(v: unknown): v is ChatOutboxEntry {
  const e = v as ChatOutboxEntry;
  return (
    !!e && typeof e.id === 'string' && typeof e.senderId === 'string' && typeof e.receiverId === 'string' &&
    typeof e.content === 'string' && typeof e.createdAt === 'string' && (e.state === 'queued' || e.state === 'failed') &&
    // The backoff counts on it: a missing count would retry at once, forever (Codex on #1136).
    Number.isSafeInteger(e.attempts) && e.attempts >= 0 &&
    (e.bookingId === null || typeof e.bookingId === 'string') && (e.error === undefined || typeof e.error === 'string')
  );
}

function announce(): void {
  const list = entries ?? [];
  for (const cb of listeners) cb(list);
}

/** Runs `task` alone on the write chain: no other save starts until it ends. */
function exclusive<T>(task: () => Promise<T>): Promise<T> {
  const run = writes.then(task);
  writes = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

/**
 * Save a list made from the one in memory as the save runs, never an older
 * copy: a later change, or the undoing of one that failed, is never
 * overwritten by a snapshot taken before it (Codex on #1136).
 */
async function save(list: ChatOutboxEntry[] = entries ?? []): Promise<void> {
  await platform.storage.secure.set(CHAT_OUTBOX_KEY, JSON.stringify(list));
}

/**
 * A change that holds only once saved: made on the list as the save runs,
 * saved with nothing else on the chain, and only then made in memory. A save
 * that fails leaves memory as it was, so no later save can keep a change the
 * caller was told failed, and rejects. Resolves false when `change` had
 * nothing to do (null) or a sign-out came first.
 */
function commitDurably(
  change: (list: ChatOutboxEntry[]) => ChatOutboxEntry[] | null,
  began = generation,
): Promise<boolean> {
  return exclusive(async () => {
    if (began !== generation) return false;
    const next = change(entries ?? []);
    if (!next) return false;
    await save(next);
    if (began !== generation) return false;
    entries = change(entries ?? []) ?? entries;
    announce();
    return true;
  });
}

/**
 * A state change after the message was kept (attempts, Try again): made in
 * memory at once; a failed save is logged, and the next save carries it.
 */
function update(id: string, patch: Partial<ChatOutboxEntry>): Promise<void> {
  if (!entries) return Promise.resolve();
  entries = entries.map((e) => (e.id === id ? { ...e, ...patch } : e));
  announce();
  return exclusive(() => save()).catch((error) => logger.warn('[chat-outbox] could not save the outbox', error));
}

/** Every entry, in the order they will be sent; none while the store cannot be read. */
export async function chatOutboxEntries(): Promise<readonly ChatOutboxEntry[]> {
  if (!IS_NATIVE) return [];
  try {
    return await load();
  } catch (error) {
    logger.warn('[chat-outbox] could not read the outbox', error);
    return [];
  }
}

export function subscribeChatOutbox(cb: (entries: readonly ChatOutboxEntry[]) => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** Told once per message the server has confirmed. */
export function onChatMessageSent(cb: (entry: ChatOutboxEntry) => void): () => void {
  sentListeners.add(cb);
  return () => sentListeners.delete(cb);
}

/**
 * Keep a message to send, then try to send it. Resolves once it is kept, not
 * once it is sent: the caller shows it as waiting until `onChatMessageSent`.
 */
export async function enqueueChatMessage(
  message: Omit<ChatOutboxEntry, 'state' | 'attempts' | 'error' | 'createdAt'> & { createdAt?: string },
): Promise<ChatOutboxEntry> {
  if (!IS_NATIVE) throw new Error('The chat outbox is app only.');
  const began = generation;
  await load();
  const entry: ChatOutboxEntry = {
    ...message,
    createdAt: message.createdAt ?? new Date().toISOString(),
    state: 'queued',
    attempts: 0,
  };
  // Kept before it is queued (Codex on #1136): it enters memory, where a
  // drain or another save can see it, only once its own save succeeded, so a
  // message the caller is told was not kept is never saved by a later write
  // and sent after a restart. The caller keeps the text and says so.
  let tooMany = false;
  let kept: boolean;
  try {
    // Under the session store lock, and only while this sender's session is
    // the one stored (Codex on #1136): a sign-out's wipe holds the lock from
    // its first check to its clear, so a message cannot be kept in the middle
    // of it and then erased by the clear after the composer was told it was
    // kept; once the wipe is done no session is stored and it is refused. A
    // store that cannot be read refuses it too (Codex on #1136).
    kept = await underSessionStoreLock(async () => {
      if (await anotherSessionStored(entry.senderId)) return false;
      return commitDurably((list) => {
        // The list as it is when the save runs: a drain may have changed it.
        // Only this sender's messages count: another account's left behind by
        // a sign-out whose wipe failed cannot fill it (Codex on #1137).
        if (list.filter((e) => e.senderId === entry.senderId).length >= CHAT_OUTBOX_LIMIT) {
          tooMany = true;
          return null;
        }
        return list.some((e) => e.id === entry.id) ? list : [...list, entry];
      }, began);
    });
  } catch {
    throw new Error('The message could not be kept to send. Please try again.');
  }
  if (tooMany) throw new Error('Too many messages are waiting to send. Reconnect, or delete some, first.');
  if (!kept) throw new Error('Signed out before the message was kept.');
  void drainChatOutbox(entry.senderId);
  return entry;
}

/**
 * Whether the store holds no session, or another sender's. A store that
 * cannot be read rejects (Codex on #1136): it may hold another account's
 * session by now, so the message is not kept and the composer keeps the text.
 */
async function anotherSessionStored(senderId: string): Promise<boolean> {
  if (!deps) return false;
  return (await deps.activeSession())?.userId !== senderId;
}

/**
 * Send a message the server refused once more. False when its "not sent"
 * mark could not be cleared (Codex on #1136): queued anyway, a restart would
 * read the mark and turn it back to "not sent", silently undoing the
 * person's Try again, so it stays "not sent" and the caller says so.
 */
export async function retryChatMessage(id: string, senderId: string): Promise<boolean> {
  if (!entries) return false; // a message shown as not sent was read from a loaded outbox
  try {
    await clearRefusedMark(id);
  } catch (error) {
    logger.warn('[chat-outbox] could not clear a refused message; not retried', error);
    return false;
  }
  // Waiting again only once that is saved (Codex on #1136): a retry that
  // seemed to work but was undone by a restart would be lost silently.
  let queued = false;
  try {
    queued = await commitDurably((list) =>
      list.some((e) => e.id === id)
        ? list.map((e) => (e.id === id ? { ...e, state: 'queued' as const, attempts: 0, error: undefined } : e))
        : null,
    );
  } catch (error) {
    logger.warn('[chat-outbox] could not save a retried message; not retried', error);
  }
  if (!queued) {
    // Still "not sent": put back the mark that keeps a restart from sending
    // it, or hold it if that fails too.
    const entry = entries?.find((e) => e.id === id);
    if (entry) await keepRefusals(new Map([[id, entry.error ?? 'Message not sent.']]));
    return false;
  }
  unsavedRefusals.delete(id);
  void drainChatOutbox(senderId);
  return true;
}

/** Drop a message that is not sent. */
export async function discardChatMessage(id: string): Promise<void> {
  if (!entries) return;
  try {
    if (!(await commitDurably((list) => list.filter((e) => e.id !== id)))) return;
  } catch {
    // The saved outbox may still hold it as waiting: the refused mark is
    // what keeps a restart from sending it, so it stays (Codex on #1136),
    // and so does the message, on screen, until the deletion is saved.
    return;
  }
  unsavedRefusals.delete(id);
  await unmarkRefused(id);
}

function scheduleRetry(senderId: string, attempts: number): void {
  // One timer, at the soonest time asked: a new message's first retry is not
  // held behind an older one's long backoff (Codex on #1136). It drains for
  // every account that asked.
  retrySenders.add(senderId);
  const delay = Math.min(15_000 * 2 ** Math.max(0, attempts - 1), 10 * 60_000);
  const at = Date.now() + delay;
  if (retryTimer && retryAt <= at) return;
  if (retryTimer) clearTimeout(retryTimer);
  retryAt = at;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    const senders = [...retrySenders];
    retrySenders.clear();
    for (const sender of senders) void drainChatOutbox(sender);
  }, delay);
}

/**
 * Send what is waiting, as `senderId`, oldest first, one at a time: only one
 * drain runs at once, and a call during one runs again after it. Stops at the
 * first attempt that gets no answer, keeping the order for the next try.
 */
export function drainChatOutbox(senderId: string | null | undefined): Promise<void> {
  if (!IS_NATIVE || !senderId) return Promise.resolve();
  // Taken out when its pass starts, so a call during that pass runs another.
  drainSenders.delete(senderId);
  drainSenders.add(senderId);
  if (draining) return draining;
  draining = (async () => {
    try {
      for (let next = first(drainSenders); next !== undefined; next = first(drainSenders)) {
        drainSenders.delete(next);
        await drainOnce(next);
      }
    } finally {
      // In the same step as the last check: a call after it starts a new drain.
      draining = null;
    }
  })();
  return draining;
}

function first<T>(set: Set<T>): T | undefined {
  for (const value of set) return value;
  return undefined;
}

async function drainOnce(senderId: string): Promise<void> {
  if (!deps) return;
  try {
    await load();
  } catch (error) {
    // The store cannot be read now: try again later rather than send from a guess.
    logger.warn('[chat-outbox] could not read the outbox', error);
    scheduleRetry(senderId, 1);
    return;
  }
  // Only as the account whose session is stored now (Codex on #1136). A
  // timer for another account leaves its messages waiting, untouched.
  if (!(await sessionToken(senderId))) return;
  await dropOtherAccounts(senderId);
  await keepUnsavedRefusals();
  if (unsavedRefusals.size > 0) scheduleRetry(senderId, 1);
  const due = (entries ?? [])
    .filter((e) => e.senderId === senderId && e.state === 'queued' && !unsavedRefusals.has(e.id))
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  for (const { id } of due) {
    // Re-read before each send: it may have been deleted, or the outbox
    // forgotten at sign-out, while the one before it was in flight.
    const entry = entries?.find((e) => e.id === id);
    if (!entry || entry.state !== 'queued' || entry.senderId !== senderId || unsavedRefusals.has(id)) continue;
    // Checked again before each send: the session may change mid-pass. The
    // send carries the token read with it.
    const accessToken = await sessionToken(senderId);
    if (!accessToken) return;
    // Deleted, refused or forgotten while that was read: not sent.
    const still = entries?.find((e) => e.id === id);
    if (!still || still.state !== 'queued' || unsavedRefusals.has(id)) continue;
    let error: ChatOutboxError | null;
    try {
      ({ error } = await deps.insert({
        id: entry.id,
        content: entry.content,
        sender_id: entry.senderId,
        receiver_id: entry.receiverId,
        message_type: 'text',
        booking_id: entry.bookingId,
      }, accessToken));
    } catch (thrown) {
      error = { message: thrown instanceof Error ? thrown.message : String(thrown) };
    }

    let sent = !error;
    let stored: Stored | null = null;
    if (error?.code === '23505') {
      const found = await deps.exists(entry.id, senderId, accessToken).catch(() => null);
      if (found === null) {
        error = { message: 'Could not check whether the message was sent.' };
      } else {
        sent = !!found;
        if (found) stored = found;
        else error = { code: '23505', message: NOT_SENT_UNCONFIRMED };
      }
    } else if (sent) {
      // Its server time and content, for the thread (Codex on #1136). A read
      // that fails keeps the message waiting, not shown as kept: the
      // moderation trigger may have rewritten it, and the realtime echo that
      // would correct it is dropped once the thread has the id (Codex on
      // #1136). The retry finds it stored, and settles it from the server's copy.
      const found = await deps.exists(entry.id, senderId, accessToken).catch(() => null);
      if (found) stored = found;
      else if (found === null) {
        sent = false;
        error = { message: 'Could not read the sent message back.' };
      }
    } else if (error?.code) {
      // Any answer from the server to a retry may be about the copy already
      // stored: an earlier attempt that committed but whose answer was lost
      // can make the moderation trigger refuse this one, or the send-rate
      // limit count it (Codex on #1136). This sender's message with this id
      // stored means it was sent. A lookup that fails tells nothing yet: a
      // refusal then waits rather than shows "not sent".
      const found = await deps.exists(entry.id, senderId, accessToken).catch(() => null);
      if (found) {
        sent = true;
        stored = found;
      } else if (found === null && !isTransientError(error)) {
        error = { message: 'Could not check whether the message was sent.' };
      }
    }

    if (sent) {
      await settleSent(entry, stored);
      continue;
    }
    if (error && isTransientError(error)) {
      const attempts = entry.attempts + 1;
      await update(entry.id, { attempts });
      scheduleRetry(senderId, attempts);
      return;
    }
    await markRefused(entry.id, refusalReason(error));
    // Neither store took it: held, shown waiting, until a drain saves it.
    // Nothing else may come to drain, so one is due (Codex on #1136).
    if (unsavedRefusals.has(entry.id)) scheduleRetry(senderId, 1);
  }
}

/**
 * The access token of the session stored now, if it is this sender's; null
 * otherwise. A read that fails is not "signed out": nothing is sent now, and
 * the drain tries again on its backoff (Codex on #1136).
 */
async function sessionToken(senderId: string): Promise<string | null> {
  if (!deps) return null;
  try {
    const session = await deps.activeSession();
    return session?.userId === senderId ? session.accessToken : null;
  } catch (error) {
    logger.warn('[chat-outbox] could not read whose session is stored; will try again', error);
    scheduleRetry(senderId, 1);
    return null;
  }
}

/**
 * Messages of another account, once this sender's session is confirmed:
 * sign-out drops an account's outbox, so these are left only by a wipe that
 * failed or stopped, and no screen of this account can send or delete them
 * (Codex on #1137). Dropped once that is saved.
 */
async function dropOtherAccounts(senderId: string): Promise<void> {
  let dropped: string[] = [];
  // Under the session store lock (lib/sessionStoreLock.ts): no sign-in can
  // store another session, and so no other account can keep a message,
  // between the check that this sender's session is the stored one and the
  // save that drops the rest (Codex on #1137).
  const saved = await underSessionStoreLock(async () => {
    if (!(await sessionToken(senderId))) return false;
    return commitDurably((list) => {
      dropped = list.filter((e) => e.senderId !== senderId).map((e) => e.id);
      return dropped.length > 0 ? list.filter((e) => e.senderId === senderId) : null;
    });
  }).catch(() => false);
  if (!saved) return;
  for (const id of dropped) unsavedRefusals.delete(id);
  try {
    await changeRefused((ids) => dropped.filter((id) => ids.delete(id)).length > 0);
  } catch {
    // A mark left for a message that is gone matches nothing.
  }
}

/**
 * Sign-out: drop the copy held in memory and let its writes settle, so none
 * lands after secure storage is cleared and puts the outbox back for the next
 * account. A drain still running finds nothing left to send or to save.
 */
export async function forgetChatOutbox(): Promise<void> {
  generation += 1;
  entries = [];
  unsavedRefusals.clear();
  if (retryTimer) clearTimeout(retryTimer);
  retryTimer = null;
  retrySenders.clear();
  announce();
  // A load still running may yet keep an unreadable outbox aside: it ends
  // first, so its write cannot land after the removals below and leave the
  // ended account's outbox behind (Codex on #1137).
  await loading?.catch(() => undefined);
  await writes;
  await underMarks(() => writeRefused(new Set()));
  // Its own key too, not only the bulk clear after it (as #1134 found): a
  // clear that fails is logged and sign-out goes on.
  try {
    await platform.storage.secure.remove(CHAT_OUTBOX_KEY);
    await platform.storage.secure.remove(CHAT_OUTBOX_UNREADABLE_KEY);
  } catch (error) {
    logger.warn('[chat-outbox] could not remove the outbox', error);
  }
}

// Every end of a session, idle expiry and the app lock's included.
onSessionWipe(forgetChatOutbox);

/** Resolves when no drain is running; never starts one. */
export async function whenChatOutboxIdle(): Promise<void> {
  while (draining) await draining;
}

/** Tests only: forget everything held in memory. */
export function resetChatOutboxForTests(): void {
  entries = null;
  loading = null;
  writes = Promise.resolve();
  draining = null;
  drainSenders.clear();
  if (retryTimer) clearTimeout(retryTimer);
  retryTimer = null;
  retrySenders.clear();
  listeners.clear();
  sentListeners.clear();
  unsavedRefusals.clear();
  deps = null;
}
